import type { ReactNode } from 'react';
import type { SvgIconComponent } from '@mui/icons-material';
import HomeOutlined from '@mui/icons-material/HomeOutlined';
import SpaceDashboardOutlined from '@mui/icons-material/SpaceDashboardOutlined';
import AutoAwesomeOutlined from '@mui/icons-material/AutoAwesomeOutlined';
import MenuBookOutlined from '@mui/icons-material/MenuBookOutlined';
import ChatBubbleOutlineRounded from '@mui/icons-material/ChatBubbleOutlineRounded';
import CalendarMonthOutlined from '@mui/icons-material/CalendarMonthOutlined';
import HubOutlined from '@mui/icons-material/HubOutlined';
import GroupOutlined from '@mui/icons-material/GroupOutlined';
import InsightsOutlined from '@mui/icons-material/InsightsOutlined';
import TuneOutlined from '@mui/icons-material/TuneOutlined';
import FactCheckOutlined from '@mui/icons-material/FactCheckOutlined';
import PsychologyOutlined from '@mui/icons-material/PsychologyOutlined';
import ExtensionOutlined from '@mui/icons-material/ExtensionOutlined';
import SettingsOutlined from '@mui/icons-material/SettingsOutlined';
import BusinessOutlined from '@mui/icons-material/BusinessOutlined';
import ForumOutlined from '@mui/icons-material/ForumOutlined';
import TimerOutlined from '@mui/icons-material/TimerOutlined';

/** The menu entries the side drawer lists (Layout's NavItem, as far as the tabs need it). */
export interface MobileNavEntry {
  label: string | ReactNode;
  route?: string;
  disabled?: boolean;
  isSectionTitle?: boolean;
}

export interface MobileTab {
  route: string;
  label: string;
  Icon: SvgIconComponent;
}

/** The bottom bar holds this many destinations, plus Menu for everything else. */
const MAX_TABS = 3;
const COMMUNICATION_ROUTES = ['/app/chat', '/app/calendar', '/app/community'];

/** Icon and short label per destination, matched on the route (labels are renamed often, routes are not). */
const TAB_LOOKS: Array<{ match: RegExp; Icon: SvgIconComponent; label?: string }> = [
  { match: /^\/app\/home\/user$/, Icon: HomeOutlined },
  { match: /\/admin\/overview$/, Icon: SpaceDashboardOutlined, label: 'Dashboard' },
  { match: /\/admin\/insights(-v3)?$/, Icon: InsightsOutlined },
  { match: /^\/app\/charlie$/, Icon: AutoAwesomeOutlined },
  { match: /^\/app\/(library|resources)$/, Icon: MenuBookOutlined },
  { match: /^\/app\/chat$/, Icon: ChatBubbleOutlineRounded },
  { match: /^\/app\/calendar$/, Icon: CalendarMonthOutlined },
  { match: /^\/app\/community$/, Icon: HubOutlined },
  { match: /^\/app\/org\/[^/]+\/members$/, Icon: GroupOutlined },
  { match: /^\/app\/capability-setup$/, Icon: TuneOutlined },
  { match: /^\/app\/operations$/, Icon: FactCheckOutlined },
  { match: /^\/app\/knowledge$/, Icon: PsychologyOutlined },
  { match: /^\/app\/integrations$/, Icon: ExtensionOutlined },
  { match: /^\/app\/orgsettings$/, Icon: SettingsOutlined },
  { match: /^\/app\/organizations$/, Icon: BusinessOutlined, label: 'Orgs' },
  { match: /^\/app\/elliott-conversations$/, Icon: ForumOutlined, label: 'Elliott Chats' },
  { match: /^\/app\/course-creation-time$/, Icon: TimerOutlined, label: 'Build Time' }
];

function lookFor(route: string) {
  return TAB_LOOKS.find((look) => look.match.test(route));
}

const ADMIN_BOARD_ROUTE = /^\/app\/programdetails\/([^/]+)\/([^/]+)\/admin\/(.+)$/;

/**
 * The menu links the admin board as /programdetails/:orgId/:coId/admin/<page>,
 * but the app also lands there with the org's group id in place of `admin`
 * (RoleRedirect). Both are the same board page.
 */
function normalisePath(route: string, pathname: string): string {
  const path = pathname.replace(/\/+$/, '') || '/';
  const board = route.match(ADMIN_BOARD_ROUTE);
  if (!board) return path;
  const landed = path.match(/^\/app\/programdetails\/([^/]+)\/([^/]+)\/[^/]+\/(.+)$/);
  return landed && landed[1] === board[1] && landed[2] === board[2]
    ? `/app/programdetails/${landed[1]}/${landed[2]}/admin/${landed[3]}`
    : path;
}

/** Whether `pathname` is on (or below) a menu destination. The learner dashboard owns every /app/home page. */
export function isRouteActive(route: string, pathname: string): boolean {
  const path = normalisePath(route, pathname);
  if (route === '/app/home/user') return /^\/app\/home(\/|$)/.test(path);
  return path === route || path.startsWith(`${route}/`);
}

/**
 * Whether `pathname` is a menu destination itself (a "root" screen, no back
 * button) rather than a screen opened from one (a lesson, a conversation, a
 * profile), which gets a back button in the phone header.
 */
export function isRootRoute(route: string, pathname: string): boolean {
  return normalisePath(route, pathname) === route;
}

/**
 * The bottom tabs for the current menu: its first destinations. Learner:
 * Dashboard, Elliott, Resources. Admin: Dashboard, People, Insights. Everything
 * else, Chat included, stays one tap away under Menu.
 */
export function pickMobileTabs(entries: MobileNavEntry[]): MobileTab[] {
  const routed = entries.filter((e): e is MobileNavEntry & { route: string } => !!e.route && !e.isSectionTitle && !e.disabled);
  const picked = routed.filter((e) => !COMMUNICATION_ROUTES.includes(e.route)).slice(0, MAX_TABS);
  return picked.flatMap((entry) => {
    const look = lookFor(entry.route);
    if (!look) return [];
    const label = look.label || (typeof entry.label === 'string' ? entry.label : '');
    return label ? [{ route: entry.route, label, Icon: look.Icon }] : [];
  });
}
